import { type Group, type Object3D } from "three"
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import type { RoadEntity } from "../../core/entity/EntityType/Road"
import { RoadKind } from "../../core/RoadKind.enum"
import { HALF_PI } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import { Road_Paved_PavedRoadCorner1_GLTF_Model, Road_Paved_PavedRoadFourWay1_GLTF_Model, Road_Paved_PavedRoadStraight1_GLTF_Model, Road_Paved_PavedRoadThreeWay1_GLTF_Model } from "../gltf_models.generated"
import { appGltfLoader } from "./appGltfLoader"
import onGltfLoaderError from "./onGltfLoaderError"

const roadModelMap: Record<RoadKind, string> = {
  [RoadKind.FourWay]: Road_Paved_PavedRoadFourWay1_GLTF_Model,
  [RoadKind.ThreeWay]: Road_Paved_PavedRoadThreeWay1_GLTF_Model,
  [RoadKind.Corner]: Road_Paved_PavedRoadCorner1_GLTF_Model,
  [RoadKind.Straight]: Road_Paved_PavedRoadStraight1_GLTF_Model,
}

export default function makeRoadMeshFromRoadEntity(
  engine: Engine,
  entNode: EntityGraphNode<RoadEntity>,
  callback: (newGroup: Object3D) => void
): void {
  const roadKind = entNode.raw().road
  const modelUrl = roadModelMap[roadKind]
  if (!modelUrl) {
    throw new Error("Unhandled road kind")
  }

  loadRoadModel(engine, modelUrl, roadKind, callback)
}

function loadRoadModel(
  engine: Engine,
  modelUrl: string,
  roadKind: RoadKind,
  callback: (newGroup: Object3D) => void,
): void {
  appGltfLoader.load(
    modelUrl,
    function (ModelObj) {
      const group = getGroup(ModelObj)
      if (roadKind !== RoadKind.Straight) {
        group.rotateY(HALF_PI)
      }
      mountOnFrameUpdateToObject3D(engine, group, function (elapsedMs: number) {
        applyEntityRotationToNextObject3DFrame(group, elapsedMs)
      })
      callback(group)
    },
    undefined,
    onGltfLoaderError
  )
}

function getGroup(ModelObj: GLTF): Group {
  const group = ModelObj.scene.getObjectByName('Road') as Group
  if (!group) {
    throw new Error("!group")
  }
  return group
}
